/**
 * TCPA quiet hours: no telemarketing/political calls or texts before 8:00 AM
 * or after 9:00 PM in the *recipient's* local time. Many states (FL, OK) are
 * stricter. We default to a conservative 9:00 AM – 8:00 PM local window and
 * let each campaign override.
 */

export interface QuietHoursPolicy {
  startHour: number; // inclusive (0-23) local
  endHour: number;   // exclusive (0-23) local
  // IANA tz of the recipient if known; else fall back to the campaign default.
  defaultTimezone: string;
}

export const DEFAULT_QUIET_HOURS: QuietHoursPolicy = {
  startHour: 9,
  endHour: 20,
  defaultTimezone: "America/New_York",
};

export function isWithinSendWindow(
  now: Date,
  policy: QuietHoursPolicy,
  recipientTimezone?: string,
): { allowed: boolean; reason?: string; localHour: number } {
  const tz = recipientTimezone ?? policy.defaultTimezone;
  let localHour: number;
  try {
    const fmt = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hour: "numeric",
      hour12: false,
    });
    localHour = parseInt(fmt.format(now), 10);
  } catch {
    localHour = now.getHours();
  }

  if (localHour < policy.startHour || localHour >= policy.endHour) {
    return {
      allowed: false,
      reason: `Outside send window (${policy.startHour}:00–${policy.endHour}:00 ${tz}); current local hour ${localHour}`,
      localHour,
    };
  }
  return { allowed: true, localHour };
}
